'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  videoUrl?: string;
}

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    role: 'CEO',
    company: 'HealthTech Solutions',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d07eb19-1763299289544.png",
    alt: 'Professional woman with blonde hair in navy blazer smiling confidently in modern office',
    rating: 5,
    text: 'Centronics IT Soloutions transformed our IT infrastructure completely. Their team\'s expertise in healthcare compliance and cybersecurity gave us the confidence to scale our operations. The 24/7 support has been invaluable during critical moments.',
    videoUrl: '#'
  },
  {
    id: 2,
    name: 'Michael Chen',
    role: 'CTO',
    company: 'FinanceFirst Bank',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4891e07-1763295537017.png",
    alt: 'Asian businessman in gray suit with glasses standing in corporate office lobby',
    rating: 5,
    text: 'Working with Centronics IT Soloutions has been a game-changer for our financial institution. Their understanding of regulatory compliance and security protocols is exceptional. We\'ve seen a 40% reduction in downtime since partnering with them.',
    videoUrl: '#'
  },
  {
    id: 3,
    name: 'Jennifer Rodriguez',
    role: 'Operations Director',
    company: 'Manufacturing Plus',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11094aa78-1763296768439.png",
    alt: 'Hispanic woman with dark hair in professional attire smiling in industrial facility',
    rating: 5,
    text: 'The cloud migration project was seamless thanks to Centronics IT Soloutions. They took the time to understand our manufacturing processes and designed a solution that improved efficiency by 35%. Their ongoing support is outstanding.',
    videoUrl: '#'
  },
  {
    id: 4,
    name: 'David Thompson',
    role: 'Founder',
    company: 'RetailHub Inc',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_183ec328e-1763295632077.png",
    alt: 'Caucasian man with beard in casual business attire standing in modern retail space',
    rating: 5,
    text: 'As a growing startup, we needed IT solutions that could scale with us. Centronics IT Soloutions provided exactly that. Their strategic guidance and flexible approach have been instrumental in our 200% growth over the past year.',
    videoUrl: '#'
  }];


  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              Real results from real businesses
            </p>
          </div>
          <div className="max-w-4xl mx-auto bg-muted rounded-2xl p-8 h-96 animate-pulse" />
        </div>
      </section>);

  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-20 bg-card">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Real results from real businesses
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-8 lg:p-12 shadow-card">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              {/* Client Image */}
              <div className="flex-shrink-0">
                <div className="relative w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden border-4 border-accent shadow-elevated">
                  <AppImage
                    src={currentTestimonial.image}
                    alt={currentTestimonial.alt}
                    className="w-full h-full object-cover" />

                </div>
              </div>

              {/* Testimonial Content */}
              <div className="flex-1 text-center lg:text-left">
                {/* Rating Stars */}
                <div className="flex justify-center lg:justify-start space-x-1 mb-4">
                  {[...Array(currentTestimonial.rating)].map((_, i) =>
                  <Icon key={i} name="StarIcon" size={20} variant="solid" className="text-warning" />
                  )}
                </div>

                {/* Quote */}
                <blockquote className="text-lg text-foreground font-body mb-6 italic">
                  &ldquo;{currentTestimonial.text}&rdquo;
                </blockquote>

                {/* Client Info */}
                <div className="mb-4">
                  <p className="font-headline font-bold text-xl text-primary">
                    {currentTestimonial.name}
                  </p>
                  <p className="text-sm text-muted-foreground font-body">
                    {currentTestimonial.role} at {currentTestimonial.company}
                  </p>
                </div>

                {/* Video Link */}
                {currentTestimonial.videoUrl &&
                <button className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 hover:bg-accent/20 text-accent rounded-lg font-cta font-semibold text-sm transition-colors duration-200">
                    <Icon name="PlayCircleIcon" size={20} variant="solid" />
                    <span>Watch Video Testimonial</span>
                  </button>
                }
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-center mt-8 space-x-4">
              <button
                onClick={prevTestimonial}
                className="w-10 h-10 flex items-center justify-center bg-white hover:bg-accent hover:text-white text-primary rounded-full shadow-card transition-all duration-200"
                aria-label="Previous testimonial">

                <Icon name="ChevronLeftIcon" size={20} variant="outline" />
              </button>

              {/* Dots Indicator */}
              <div className="flex space-x-2">
                {testimonials.map((_, index) =>
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-accent w-8' : 'bg-border hover:bg-accent/50'}`
                  }
                  aria-label={`Go to testimonial ${index + 1}`} />

                )}
              </div>

              <button
                onClick={nextTestimonial}
                className="w-10 h-10 flex items-center justify-center bg-white hover:bg-accent hover:text-white text-primary rounded-full shadow-card transition-all duration-200"
                aria-label="Next testimonial">

                <Icon name="ChevronRightIcon" size={20} variant="outline" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialCarousel;